// ---------------------------------------------------------------------------
// The point of sale.
//
// Left: search, category tabs and the product grid.
// Right: the current order.
//
// Built for a touchscreen first. The search box takes focus on load so a
// barcode scanner - which simply types very fast and presses Enter - works
// with no extra wiring: scan, and if exactly one product matches it is added
// straight away.
//
// ONE Current Order, and the FEE decides what it is. A zero fee is a counter
// sale - items, payment, receipt, done. A positive fee IS a delivery: the
// checkout button becomes «إنشاء طلب التوصيل», the frozen order goes out with
// a printed receipt and no money, and the account is collected in the
// Delivery View when the driver returns. There is no second delivery form, no
// customer requirement and no mode toggle - the old delivery panel asked for
// a customer, an address and a due-time that the real workflow never used.
//
// Three things are deliberately not done here:
//
//   * Holding a cart reserves NOTHING. An abandoned lunch-rush cart must never
//     lock up meat the shop could sell to the next person through the door.
//   * A restored cart is never silently re-priced. If anything moved while it
//     waited, the cashier is shown what and has to say they have told the
//     customer.
//   * The delivery fee never becomes a cart line. It moves no stock and
//     belongs to no category; as a line it would quietly corrupt every
//     by-product and by-category report the shop runs.
//
// THE LAYOUT
//
// One grid, two tracks, both `minmax(0, …)`. That zero is the whole point: the
// default is `min-content`, which means a track refuses to shrink below its
// widest child, and one long Arabic product name is then enough to push the
// Current Order off the side of a 1093-pixel laptop. Every scrolling region is
// an internal `overflow` container, so the PAGE never scrolls sideways and
// nothing that matters - the totals, Take Payment - ever scrolls out of reach.
//
// Below the `till` breakpoint the second track disappears and the order moves
// into a drawer, with a permanent bar carrying the item count and the running
// total. It is the same CartPanel in both places, not a second implementation.
// ---------------------------------------------------------------------------

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AlertCircle, PauseCircle, Search, Truck, X } from 'lucide-react';
import type { ComboOffer, Customer, OnlineOrder } from '../types';
import clsx from 'clsx';
import { useI18n } from '../i18n/I18nProvider';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import { useCategories } from '../api/catalog.api';
import { useActiveCombos, useOffersCatalogue } from '../api/pricing.api';
import { useExchangeRate, usePublicSettings } from '../api/system.api';
import { useCompleteSale, useCurrentShift } from '../api/sales.api';
import {
  useCustomer as useCustomerRecord,
  useDispatchDelivery,
  useOrder,
} from '../api/customers.api';
import { useHoldCart, useRestoreHeldOrder } from '../api/heldOrders.api';
import { api, ApiError } from '../lib/apiClient';
import {
  cartLineFrom,
  cartSubtotal,
  cartSubtotalLbp,
  cartNeedsPreparation,
  useCartStore,
  type CartLine,
  type QuantityMode,
} from '../stores/cartStore';
import { toast } from '../stores/toastStore';
import { ProductGrid } from '../components/pos/ProductGrid';
import { CartPanel } from '../components/pos/CartPanel';
import { QuantityKeypad } from '../components/pos/QuantityKeypad';
import { PaymentDialog } from '../components/pos/PaymentDialog';
import { CustomerPicker } from '../components/pos/CustomerPicker';
import { NetworkPrintSheets } from '../components/pos/NetworkPrintSheets';
import type { PrintPlan } from '../hooks/useSalePrinting';
import { ConfirmDialog } from '../components/common/Modal';
import { Overlay } from '../components/common/Overlay';
import { EmptyState, ErrorState } from '../components/common/StateBlocks';
import { usePermission } from '../hooks/usePermissions';
import { useBusinessDay } from '../hooks/useBusinessDay';
import { BusinessDayEndedDialog } from '../components/pos/BusinessDayEndedDialog';
// --- Phase 9: selling with no connection ----------------------------------
import { usePosCatalogue } from '../hooks/usePosCatalogue';
import { isEffectivelyOffline, useOfflineStore } from '../stores/offlineStore';
import { enqueueSale } from '../lib/syncEngine';
import { deviceId, newOfflineId, offlineStorageAvailable, takeReservedReceipt } from '../lib/offlineDb';
import { offlineReceiptSale } from '../utils/offlineReceipt';
import { OfflineReceiptPrint } from '../components/pos/OfflineReceiptPrint';
import { useAuthStore } from '../stores/authStore';
import { OfflineSaleConfirmation } from '../components/pos/OfflineSaleConfirmation';
// --- operational changes: holds, delivery, preparation tickets -------------
import { HoldOrderDialog, type HoldDetails } from '../components/pos/HoldOrderDialog';
import { HeldOrdersDrawer } from '../components/pos/HeldOrdersDrawer';
import { RestoreWarningsDialog } from '../components/pos/RestoreWarningsDialog';
import { DeliveryDispatchDialog } from '../components/pos/DeliveryDispatchDialog';
import {
  DeliveryAccountsPanel,
  PosViewToggle,
  usePendingAccountsCount,
  type PosView,
} from '../components/pos/DeliveryAccountsPanel';
import { CollectAccountDialog } from '../components/pos/CollectAccountDialog';
import { ConfirmedMenuOrder } from '../components/pos/ConfirmedMenuOrder';
import { useCollectDelivery, useConfirmOnlineDeliveryFee } from '../api/customers.api';
import { MenuOrderArrival } from '../components/pos/MenuOrderArrival';
import { MenuOrderReviewBanner, useMenuOrderReview } from '../components/pos/MenuOrderReview';
import { useOnlineOrder } from '../api/menu.api';
import { SaleReceipt } from '../components/pos/SaleReceipt';
import { dispatchReceiptSale } from '../utils/orderReceipt';
import { CategoryRail } from '../components/pos/CategoryRail';
import { CartDrawer, CartSummaryBar } from '../components/pos/CartDrawer';
import { formatDateTime, formatLbp, formatQuantity, formatUsd, toDecimal } from '../utils/format';
import type {
  Currency,
  CustomerOrder,
  PaymentDraft,
  Product,
  ProductVariant,
  RestoreResult,
  Sale,
} from '../types';

/// A key identifying one attempt at one sale.
///
/// Generated when the payment dialog opens and reused for every retry of that
/// same order, which is what lets the server tell a retry apart from a genuine
/// second sale. `crypto.randomUUID` is available in every browser this runs
/// on; the fallback keeps tests and older environments working.
function newIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `sale-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

/// A cart line as the order endpoint wants it.
///
/// An order carries a QUANTITY, not a mode - the butcher cuts a weight, not a
/// dollar figure. So "give me $20 of kafta" has to become a weight before it
/// can go on a ticket, and the till says so on screen rather than converting
/// behind the cashier's back.
function orderQuantityFor(line: CartLine): string {
  if (line.quantityMode !== 'CUSTOM_AMOUNT') return line.quantity;

  const price = toDecimal(line.unitPrice);
  if (price.lessThanOrEqualTo(0)) return '0.000';

  return toDecimal(line.customAmount ?? '0').dividedBy(price).toDecimalPlaces(3).toFixed(3);
}

/// A transport charge typed onto a COUNTER sale: what was typed, and which
/// currency it was typed in.
interface CounterFee {
  amount: string;
  currency: Currency;
}

/// What that charge looks like on a till nobody has touched yet.
///
/// LIRA, AND ONLY LIRA. A counter delivery charge is quoted to the customer
/// out loud - "two hundred thousand to take it up the road" - so it is typed
/// in the currency it was quoted in and there is nothing to choose. It used
/// to default to dollars, which meant the cashier switched by hand on every
/// order or forgot to and charged five hundred thousand DOLLARS; then it
/// defaulted to lira with dollars a tap away, which left the same mistake one
/// tap away. The currency is now fixed, and `Currency` stays in the shape
/// only because every downstream conversion still names one.
///
/// The amount starts EMPTY, as it always has: the shop's standing delivery
/// fee belongs to delivery ORDERS, and quietly adding it to a walk-in would
/// overcharge somebody carrying their own meat home.
const freshCounterFee: CounterFee = Object.freeze({ amount: '', currency: 'LBP' });

export function PosPage() {
  const { t, locale } = useI18n();
  useDocumentTitle(t.pos.title);

  const [search, setSearch] = useState('');
  const [categoryId, setCategoryId] = useState<string | null>(null);
  /// The virtual «العروض» tab. A CHIP, not a category: it reassigns no
  /// product, routes no ticket and appears only while something is on offer.
  const OFFERS_TAB = 'virtual:offers';
  const onOffersTab = categoryId === OFFERS_TAB;
  const searchRef = useRef<HTMLInputElement>(null);

  // Waiting 250 ms after the last keystroke stops a request per character.
  const debouncedSearch = useDebouncedValue(search, 250);

  /// Only meaningful below the `till` breakpoint, where the order lives in a
  /// drawer rather than in its own column.
  const [cartDrawerOpen, setCartDrawerOpen] = useState(false);

  const [keypadProduct, setKeypadProduct] = useState<Product | null>(null);
  const [editingLine, setEditingLine] = useState<CartLine | null>(null);
  const [confirmClear, setConfirmClear] = useState(false);

  // --- checkout state ------------------------------------------------------
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  /// Generated once per checkout attempt, not per click.
  const [idempotencyKey, setIdempotencyKey] = useState<string>('');
  /// The sale just completed, shown as a receipt.
  const [completedSale, setCompletedSale] = useState<Sale | null>(null);

  /// What the sale that just completed owes the printers, as the backend
  /// described it AFTER its transaction committed. Held here rather than
  /// derived: the plan names the design VERSIONS this sale was rung up
  /// under, and those must not drift to whatever is active later.
  const [printPlan, setPrintPlan] = useState<PrintPlan | null>(null);

  /// A transport charge on a COUNTER sale, always in lira. See
  /// `freshCounterFee` for what it starts as and why.
  const counterFee = useCartStore(state => state.counterFee);
  const setCounterFee = useCartStore(state => state.setCounterFee);
  /// A sale saved on this till because there was no connection. It has no
  /// receipt number yet - the server assigns those - so it is shown
  /// differently rather than pretending it has one.
  /// The receipt of a sale rung up offline, on its way to the shop's print
  /// agent. Null between sales.
  const [offlineReceipt, setOfflineReceipt] = useState<{ offlineId: string; sale: Sale } | null>(null);
  const currentUser = useAuthStore((state) => state.user);
  const [savedOffline, setSavedOffline] = useState<{ offlineId: string; totalUsd: string } | null>(
    null,
  );

  // --- who it is for, Phase 7 ----------------------------------------------
  /// Null for a walk-in, which is most sales. Required for anything going on
  /// an account, and for every delivery.
  const customer = useCartStore(state => state.customer);
  const setCustomer = useCartStore(state => state.setCustomer);
  const [customerPickerOpen, setCustomerPickerOpen] = useState(false);
  const canSellOnCredit = usePermission('payments.credit');

  // --- holding a cart ------------------------------------------------------
  const canHold = usePermission('held_orders.create');
  const canSeeHeld = usePermission('held_orders.view_own');
  const canRestore = usePermission('held_orders.restore');
  const [holdOpen, setHoldOpen] = useState(false);
  const [holdError, setHoldError] = useState<string | null>(null);
  const [heldDrawerOpen, setHeldDrawerOpen] = useState(false);
  const [restoringId, setRestoringId] = useState<string | null>(null);
  /// A restore whose warnings the cashier has not acknowledged yet. Nothing
  /// goes on the till until they have.
  const [pendingRestore, setPendingRestore] = useState<RestoreResult | null>(null);
  const restoredCustomer = useRef<Customer | null>(null);
  /// The held cart the current till contents came from, sent with the sale so
  /// the server can mark it converted rather than leaving it on the spike.
  const restoredFromHeldId = useCartStore(state => state.restoredFromHeldId);
  const setRestoredFromHeldId = useCartStore(state => state.setRestoredFromHeldId);

  // --- delivery ------------------------------------------------------------
  const canTakeDelivery = usePermission('orders.delivery_create');
  /// The unpaid ORDER this till is currently settling - a pickup taken over
  /// the phone, or a delivery from before exact snapshots existed. New
  /// deliveries never come back here: their accounts are collected in the
  /// Delivery View, against the frozen figures.
  const [activeOrder, setActiveOrder] = useState<CustomerOrder | null>(null);
  /// The delivery that has just been dispatched: driver gone, receipt
  /// printed, account pending.
  const [sentOrder, setSentOrder] = useState<CustomerOrder | null>(null);
  const [orderError, setOrderError] = useState<string | null>(null);
  /// «إنشاء طلب التوصيل» - the confirmation the checkout button opens when
  /// the fee is positive, instead of the payment dialog.
  const [dispatchOpen, setDispatchOpen] = useState(false);
  const [dispatchError, setDispatchError] = useState<string | null>(null);
  /// The shop's standing delivery fee, offered as one tap on the fee field.
  const [standingFee, setStandingFee] = useState<string | null>(null);

  // --- the order column's two views ---------------------------------------
  /// «الطلب الحالي» or «حسابات التوصيل». A VIEW switch and nothing more: the
  /// cart lives in its store, the fee and customer live in this page's state,
  /// and none of them are touched by flipping the column - the isolation the
  /// dedicated regression tests pin.
  const canSeeAccounts = usePermission('orders.delivery_view');
  const [posView, setPosView] = useState<PosView>('ORDER');
  /// The delivery being collected from the quick panel - handed to the SAME
  /// CollectAccountDialog the full board uses. Entirely separate from the
  /// cart's own payment dialog: different entity, different state, different
  /// endpoint.
  const [collectingOrder, setCollectingOrder] = useState<CustomerOrder | null>(null);
  /// The delivery whose FROZEN receipt the cashier is looking at - the
  /// read-only preview a card tap opens, so they can verify which account
  /// the returning money belongs to. Looking writes nothing and prints
  /// nothing; the modal's own Print button is the one explicit copy.
  const [previewOrder, setPreviewOrder] = useState<CustomerOrder | null>(null);
  const pendingAccounts = usePendingAccountsCount(canSeeAccounts);

  // --- preparation tickets -------------------------------------------------
  //
  // Nothing to hold here any more. The ticket is written by the sale
  // transaction and printed straight from the plan the response carries; the
  // till neither fetches it nor shows it. Reprints live in the ticket
  // history, where somebody has gone looking for one.

  const lines = useCartStore((state) => state.lines);
  const orderNote = useCartStore((state) => state.orderNote);
  const addLine = useCartStore((state) => state.addLine);
  const addCombo = useCartStore((state) => state.addCombo);
  const updateLine = useCartStore((state) => state.updateLine);
  const removeLine = useCartStore((state) => state.removeLine);
  const clearCart = useCartStore((state) => state.clear);
  const replaceLines = useCartStore((state) => state.replaceLines);
  const setOrderNote = useCartStore((state) => state.setOrderNote);

  // --- Phase 9: which side of the connection are we on? --------------------
  const browserOnline = useOfflineStore((state) => state.browserOnline);
  const serverReachable = useOfflineStore((state) => state.serverReachable);
  const offline = isEffectivelyOffline({ browserOnline, serverReachable });

  const { data: categories } = useCategories();
  const { data: rate } = useExchangeRate();
  const { data: settings } = usePublicSettings();

  // --- what the shop has configured, applied once ------------------------
  //
  // Applied ONCE, on the first load of the settings, and never again: a
  // cashier who has switched to Delivery must not be dragged back to Counter
  // because a background refetch returned the same defaults. `posDefaults`
  // remembers that the till has already been set up.
  const posDefaults = useRef(false);

  useEffect(() => {
    if (!settings || posDefaults.current) return;
    posDefaults.current = true;

    const startingCategory = settings['pos.default_category_id'];
    if (startingCategory) setCategoryId(startingCategory);

    // The standing delivery fee, kept as a ONE-TAP chip on the fee field
    // rather than pre-filled: quietly adding it to a walk-in would overcharge
    // somebody carrying their own meat home. Only a lira figure is offered -
    // the field takes lira, and that is the currency the shop quotes.
    const fee = settings['delivery.default_fee_amount'];
    if (
      fee &&
      toDecimal(fee).greaterThan(0) &&
      settings['delivery.default_fee_currency'] === 'LBP'
    ) {
      setStandingFee(fee);
    }
  }, [settings, canTakeDelivery]);
  const { data: shiftState } = useCurrentShift();
  const completeSale = useCompleteSale();
  const holdCart = useHoldCart();
  const preserveCart = holdCart.mutateAsync;
  const restoreHeld = useRestoreHeldOrder();
  const dispatchDelivery = useDispatchDelivery();

  // WHETHER THIS DRAWER MAY STILL TAKE MONEY.
  //
  // Two separate conditions with two separate answers: there is no drawer at
  // all ("open one"), or there is one but its trading day has ended ("close
  // yesterday's first"). Sending a cashier to the wrong screen at midnight is
  // how a queue forms.
  //
  // `businessDayEnded` is the SERVER's answer, refreshed by `useBusinessDay`
  // whenever it could have changed - on foreground, on focus, on reconnect, at
  // the boundary the server named, and on a slow tick while somebody is
  // watching. Nothing here consults the machine's own clock.
  const { dayEnded: businessDayEnded } = useBusinessDay();
  const canCloseShift = usePermission('shifts.close');

  const hasOpenShift = Boolean(shiftState?.shift);
  /// The drawer is usable: it exists AND it belongs to today.
  const canTakeMoney = hasOpenShift && !businessDayEnded;

  const {
    products,
    categories: posCategories,
    isLoading,
    error,
    refetch,
    fromCache,
    capturedAt,
    unavailable: catalogueUnavailable,
  } = usePosCatalogue(
    {
      search: debouncedSearch || undefined,
      // The offers tab reads the WHOLE sellable catalogue and keeps what is
      // on promotion; a real category reads its own shelf.
      categoryId: onOffersTab ? undefined : (categoryId ?? undefined),
      pageSize: onOffersTab ? 200 : 100,
    },
    categories,
  );

  // What is on offer right now: the live combos, and the products the
  // server priced under a rule. ONE appearance per product - a product with
  // both a product and a category discount shows its product discount only,
  // because that is the rule that applies. Nothing here changes a product's
  // real category; the same card still sits on its own shelf.
  const { data: activeCombos } = useActiveCombos(!offline);
  const combos = useMemo(() => (offline ? [] : (activeCombos ?? [])), [activeCombos, offline]);
  const discountedProducts = useMemo(
    () =>
      products.filter((product) =>
        product.hasVariants
          ? (product.variants ?? []).some((v) => v.isActive && v.promotion && v.promotion.promotionType !== 'NONE')
          : Boolean(product.promotion && product.promotion.promotionType !== 'NONE'),
      ),
    [products],
  );
  // The chip's count comes from a whole-catalogue read, so it is right
  // whichever shelf is open. Cheap: the same list query the offers tab uses.
  const { data: promotedCatalogue } = useOffersCatalogue(!offline);
  const promotedProductCount = useMemo(
    () =>
      (promotedCatalogue ?? []).filter((product) =>
        product.hasVariants
          ? (product.variants ?? []).some((v) => v.isActive && v.promotion && v.promotion.promotionType !== 'NONE')
          : Boolean(product.promotion && product.promotion.promotionType !== 'NONE'),
      ).length,
    [promotedCatalogue],
  );
  const offersChipCount = combos.length + promotedProductCount;
  const shownProducts = onOffersTab
    ? [
        ...discountedProducts.filter((p) => (p.hasVariants ? false : p.promotion?.promotionType === 'PRODUCT_DISCOUNT') || (p.hasVariants && (p.variants ?? []).some((v) => v.promotion?.promotionType === 'PRODUCT_DISCOUNT'))),
        ...discountedProducts.filter((p) => (p.hasVariants ? !(p.variants ?? []).some((v) => v.promotion?.promotionType === 'PRODUCT_DISCOUNT') : p.promotion?.promotionType === 'CATEGORY_DISCOUNT')),
      ]
    : products;

  // --- always inside a category ------------------------------------------
  //
  // With the "All" tab gone the till must land on a real category by itself,
  // or the cashier would meet an empty grid on the first open. The shop's
  // configured default wins when it is still a visible category; otherwise
  // the first one in the shop's own category order.
  //
  // Runs ONLY while nothing is selected, so a cashier's choice is never
  // overridden by a background refetch - and it tolerates a default that
  // points at a category since hidden or deleted.
  useEffect(() => {
    if (posCategories.length === 0) return;
    if (categoryId === OFFERS_TAB) return;

    const stillVisible = posCategories.some((category) => category.id === categoryId);
    if (categoryId && stillVisible) return;

    const configured = settings?.['pos.default_category_id'];
    const fallback =
      posCategories.find((category) => category.id === configured) ?? posCategories[0];
    if (fallback) setCategoryId(fallback.id);
  }, [posCategories, categoryId, settings]);

  // `products` now comes from usePosCatalogue above - the server when there
  // is a connection, the device's snapshot when there is not.

  // The search box holds focus so a scanner's keystrokes land somewhere useful.
  useEffect(() => {
    searchRef.current?.focus();
  }, []);

  // =========================================================================
  // Reopening an unpaid delivery order
  // =========================================================================
  //
  // The Orders board sends the cashier here with `?orderId=`. The order is
  // fetched, its items go on the till at the prices the customer was quoted,
  // and the delivery panel is locked - the stock is already reserved against
  // those exact lines.

  const [searchParams, setSearchParams] = useSearchParams();
  const orderIdParam = searchParams.get('orderId');
  const accountsRequested = searchParams.get('view') === 'accounts';
  useEffect(() => {
    if (!accountsRequested || !canSeeAccounts) return;
    setPosView('ACCOUNTS');
    setSentOrder(null);
    setCartDrawerOpen(window.matchMedia('(max-width: 1099px)').matches);
  }, [accountsRequested, canSeeAccounts]);
  const currentOrderId = useCartStore(state => state.orderId);
  const preservationId = useCartStore(state => state.preservationId);
  const handoffOrderId = useCartStore(state => state.handoffOrderId);
  const requestedOrderId = orderIdParam ?? handoffOrderId ?? currentOrderId;
  const { data: reopenedOrder, error: reopenError } = useOrder(requestedOrderId);
  const collectCurrentOrder = useCollectDelivery();
  const confirmFee = useConfirmOnlineDeliveryFee();
  const [loadingOrder, setLoadingOrder] = useState(false);
  const [handoffAttempt, setHandoffAttempt] = useState(0);
  const preservation = useRef<Promise<void> | null>(null);
  /// The delivery the till has just sent to Delivery Accounts. Finalizing
  /// its fee invalidates the order query, and that refetch can land while
  /// the `?orderId` is still being cleared (a router transition): without
  /// this the freshly fetched order would hydrate straight back onto the
  /// till. Forgotten as soon as nothing asks for an order any more.
  const releasedOrder = useRef<string | null>(null);
  const [feeDraft, setFeeDraft] = useState('');
  const frozenOrder = Boolean(activeOrder?.onlineOrder && activeOrder.totalLbp !== null);
  const loadingContext = Boolean((requestedOrderId && requestedOrderId !== currentOrderId) || (currentOrderId && activeOrder?.id !== currentOrderId));
  const saleLocked = frozenOrder || loadingContext || loadingOrder || Boolean(preservationId);

  // =========================================================================
  // Reviewing an online menu order
  // =========================================================================
  //
  // Menu Management sends the cashier here with `?menuOrderId=`. The order's
  // FROZEN snapshot goes onto the till exactly as the customer sent it - the
  // products, the types, the weights, the notes, the combos, the prices the
  // customer accepted - and the cart is locked. Loading it creates nothing:
  // no sale, no payment, no stock movement, no paper, no number. Confirm and
  // Reject are the same backend transitions Menu Management uses.
  const menuOrderIdParam = searchParams.get('menuOrderId');
  const { data: menuOrderSource, error: menuOrderError } = useOnlineOrder(menuOrderIdParam);
  const [reviewOrder, setReviewOrder] = useState<OnlineOrder | null>(null);
  const loadedMenuOrderRef = useRef<string | null>(null);
  const beforeMenuReview = useRef<{ lines: CartLine[]; note: string } | null>(null);
  useEffect(() => () => {
    const saved = beforeMenuReview.current;
    if (saved) useCartStore.getState().replaceLines(saved.lines, saved.note);
    beforeMenuReview.current = null;
  }, [menuOrderIdParam]);
  const canConfirmMenuOrder = usePermission('menu.orders.confirm');
  const canRejectMenuOrder = usePermission('menu.orders.reject');

  const exitReview = () => {
    if (beforeMenuReview.current) replaceLines(beforeMenuReview.current.lines, beforeMenuReview.current.note);
    else clearCart();
    beforeMenuReview.current = null;
    setReviewOrder(null);
    loadedMenuOrderRef.current = null;
    if (menuOrderIdParam) setSearchParams({}, { replace: true });
  };

  const review = useMenuOrderReview({
    order: reviewOrder,
    onDone: exitReview,
    onConfirmed: id => setSearchParams({ orderId: id }, { replace: true }),
  });

  useEffect(() => {
    if (!menuOrderSource || loadedMenuOrderRef.current === menuOrderSource.id) return;
    loadedMenuOrderRef.current = menuOrderSource.id;
    if (menuOrderSource.status !== 'PENDING_CONFIRMATION') {
      toast.error(t.menu.orders.notPending, menuOrderSource.orderNumber);
      setSearchParams({}, { replace: true });
      return;
    }
    const rate = toDecimal(menuOrderSource.exchangeRateUsdToLbp);
    if (!beforeMenuReview.current) beforeMenuReview.current = { lines: useCartStore.getState().lines, note: useCartStore.getState().orderNote };
    replaceLines(
      menuOrderSource.items.map((item) =>
        cartLineFrom({
          productId: item.productId,
          variantId: item.variantId,
          nameEn: item.nameEn,
          nameAr: item.nameAr,
          variantNameEn: item.variantNameEn,
          variantNameAr: item.variantNameAr,
          variantLabelEn: item.variantLabelEn,
          variantLabelAr: item.variantLabelAr,
          sku: item.sku,
          sellingUnit: item.sellingUnit,
          // The dollars the snapshot's own rate makes of its frozen lira.
          unitPrice: rate.greaterThan(0) ? toDecimal(item.unitPriceLbp).dividedBy(rate).toDecimalPlaces(4).toFixed(4) : '0',
          unitPriceLbp: item.unitPriceLbp,
          quantityMode: item.quantityMode === 'CUSTOM_AMOUNT' ? 'COUNT' : item.quantityMode,
          quantity: item.quantity,
          note: item.notes,
          basePriceLbp: item.basePriceLbp,
          promotionType: item.promotionType,
          discountPercent: item.discountPercent,
          comboOfferId: item.comboOfferId,
          comboGroupKey: item.comboGroupKey,
          comboNameEn: item.comboNameEn,
          comboNameAr: item.comboNameAr,
          comboOfferPriceLbp: item.comboOfferPriceLbp,
          comboRegularPriceLbp: item.comboRegularPriceLbp,
          lineTotalLbp: item.lineTotalLbp,
        }),
      ),
      menuOrderSource.customerNotes ?? '',
    );
    setReviewOrder(menuOrderSource);
    toast.info(t.menu.orders.reviewTitle, menuOrderSource.orderNumber);
  }, [menuOrderSource, replaceLines, setSearchParams, t]);
  // The order carries only a thumbnail of its customer; the payment dialog
  // needs the full record to know about credit.
  const { data: orderCustomer } = useCustomerRecord(activeOrder?.customer?.id ?? null);

  useEffect(() => {
    if (!reopenedOrder) { releasedOrder.current = null; return; }
    if (reopenedOrder.id === releasedOrder.current) return;
    let disposed = false;
    const open = async () => {
      const current = useCartStore.getState();
      if (reopenedOrder.paymentStatus === 'PAID' || reopenedOrder.sale || reopenedOrder.status === 'CANCELLED') {
        if (current.orderId === reopenedOrder.id) { current.clear(); setActiveOrder(null); }
        setOrderError(reopenedOrder.status === 'CANCELLED' ? t.menu.orders.notPending : t.delivery.alreadyPaid);
        return;
      }
      if (!reopenedOrder.onlineOrder && reopenedOrder.fulfilment === 'DELIVERY' && reopenedOrder.totalLbp !== null) {
        setOrderError(t.delivery.collectInView);
        return;
      }
      if (current.orderId === reopenedOrder.id) {
        setActiveOrder(reopenedOrder);
        setOrderError(null);
        return;
      }
      setLoadingOrder(true);
      setOrderError(null);
      setPaymentOpen(false);
      setDispatchOpen(false);
      setCollectingOrder(null);
      setFeeDraft('');
      try {
        if (current.lines.length && !current.orderId) {
          // A hold has no counter-fee field: leave that sale intact until its
          // cashier finishes it. Failed/offline preservation never clears it.
          if (!canHold || offline || current.counterFee.amount.trim()) throw new Error(t.menu.orders.preserveFirst);
          if (!preservation.current) {
            const snapshot = JSON.stringify({ lines: current.lines, note: current.orderNote, customer: current.customer });
            const id = current.preservationId ?? crypto.randomUUID();
            current.setHandoffOrderId(reopenedOrder.id);
            current.setPreservationId(id);
            preservation.current = preserveCart({
              id, customerId: current.customer?.id ?? null,
              customerName: current.customer?.name ?? null, phone: current.customer?.phone ?? null,
              orderNote: current.orderNote || null,
              items: current.lines.map(line => ({ productId: line.productId, variantId: line.variantId,
                quantityMode: line.quantityMode, quantity: line.quantity,
                ...(line.quantityMode === 'CUSTOM_AMOUNT' ? { customAmount: line.customAmount ?? '0' } : {}),
                note: line.note, ...(line.comboGroupKey ? { comboOfferId: line.comboOfferId, comboGroupKey: line.comboGroupKey } : {}),
              })),
            }).then(() => {
              const latest = useCartStore.getState();
              if (snapshot !== JSON.stringify({ lines: latest.lines, note: latest.orderNote, customer: latest.customer })) throw new Error(t.menu.orders.preserveFirst);
              latest.clear();
              latest.setHandoffOrderId(reopenedOrder.id);
              toast.success(t.held.heldState, t.held.holdBody);
            }).finally(() => { preservation.current = null; });
          }
          await preservation.current;
        }
        if (disposed) return;
        useCartStore.getState().loadOrder(reopenedOrder);
        setActiveOrder(reopenedOrder);
        setPosView('ORDER');
        setCartDrawerOpen(window.matchMedia('(max-width: 1099px)').matches);
      } catch (caught) {
        if (!disposed) setOrderError(caught instanceof Error ? caught.message : t.errors.unexpectedBody);
      } finally {
        if (!disposed) setLoadingOrder(false);
      }
    };
    void open();
    return () => { disposed = true; };
  }, [reopenedOrder, canHold, offline, preserveCart, handoffAttempt, t]);

  useEffect(() => {
    if (orderCustomer && customer?.id !== orderCustomer.id) setCustomer(orderCustomer);
  }, [orderCustomer, customer, setCustomer]);

  /// Enter in the search box: if exactly one product matches, add it. This is
  /// what makes a barcode scanner work without any scanner-specific code.
  const onSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();

    if (products.length === 1 && products[0]) {
      const product = products[0];
      if (product.stockStatus === 'OUT_OF_STOCK') {
        toast.error(t.pos.outOfStock, locale === 'ar' ? product.nameAr : product.nameEn);
        return;
      }
      openKeypadFor(product);
      setSearch('');
    }
  };

  const openKeypadFor = (product: Product) => {
    // A menu order under review is frozen: nothing is added to it here.
    if (reviewOrder || saleLocked) {
      toast.info(frozenOrder ? t.menu.orders.frozenSale : t.menu.orders.frozen);
      return;
    }
    setEditingLine(null);
    setKeypadProduct(product);
  };

  const closeKeypad = () => {
    setKeypadProduct(null);
    setEditingLine(null);
    // Hand focus back so the next scan lands in the search box.
    searchRef.current?.focus();
  };

  const onKeypadSubmit = (result: {
    quantity: string;
    mode: QuantityMode;
    customAmount?: string;
    note: string;
    variant?: ProductVariant | null;
  }) => {
    if (editingLine) {
      updateLine(editingLine.id, {
        quantity: result.quantity,
        note: result.note,
        ...(result.customAmount !== undefined ? { customAmount: result.customAmount } : {}),
        // A different type on an unpaid line takes its price with it.
        ...(result.variant && result.variant.id !== editingLine.variantId
          ? { variant: result.variant }
          : {}),
      });
    } else if (keypadProduct) {
      addLine(keypadProduct, {
        quantity: result.quantity,
        mode: result.mode,
        ...(result.customAmount !== undefined ? { customAmount: result.customAmount } : {}),
        note: result.note,
        variant: result.variant ?? null,
      });
    }

    closeKeypad();
  };

  const exchangeRateValue = activeOrder?.onlineOrder ? activeOrder.exchangeRateUsdToLbp : (reviewOrder?.exchangeRateUsdToLbp ?? rate?.usdToLbp ?? null);
  const itemsSubtotal = cartSubtotal(lines, exchangeRateValue);

  /// The typed fee, tidied. The field takes LIRA and nothing else.
  const typedFeeLbp = /^\d+(\.\d{1,2})?$/.test(counterFee.amount.trim())
    ? toDecimal(counterFee.amount.trim()).toDecimalPlaces(0)
    : toDecimal(0);

  /// The fee in dollars. For an order that already exists this is the FROZEN
  /// figure the customer was quoted, not today's conversion of it - which is
  /// the whole reason the rate was stored on the order.
  const deliveryFeeUsd = activeOrder
    ? activeOrder.deliveryFeeUsd
    : exchangeRateValue && typedFeeLbp.greaterThan(0)
      ? typedFeeLbp.dividedBy(toDecimal(exchangeRateValue)).toDecimalPlaces(2).toFixed(2)
      : '0.00';

  /// The same fee in LIRA, which is what it was typed in and what is charged.
  /// The dollar figure above is what it comes to.
  const deliveryFeeLbp = activeOrder
    ? // The frozen lira when the order recorded one; the frozen dollars
      // converted at today's rate for the legacy tail that did not.
      (activeOrder.deliveryFeeLbp ??
      (exchangeRateValue
        ? toDecimal(activeOrder.deliveryFeeUsd)
            .times(toDecimal(exchangeRateValue))
            .toDecimalPlaces(0)
            .toFixed(0)
        : '0'))
    : typedFeeLbp.toFixed(0);

  /// A POSITIVE FEE IS A DELIVERY - the one business rule of this feature.
  /// True only for a NEW order: an active order already decided what it is.
  const feeMakesDelivery = !activeOrder && typedFeeLbp.greaterThan(0);

  /// What is wrong with the typed counter fee, if anything.
  ///
  /// Refused rather than clamped: silently turning a mistyped 200 into 20
  /// would put a number on a receipt that nobody chose.
  const counterFeeProblem = (() => {
    const raw = counterFee.amount.trim();
    if (raw === '') return null;
    if (/^-/.test(raw)) return t.pos.feeNegative;
    if (!/^\d+(\.\d{1,2})?$/.test(raw)) return t.pos.feeTooLarge;

    const typed = toDecimal(raw);
    if (typed.lessThan(0)) return t.pos.feeNegative;
    // A delivery charge in a butcher's shop is a few dollars. Four figures is
    // a slipped decimal point or a wrong keypress.
    const ceiling = counterFee.currency === 'USD' ? 1000 : 100_000_000;
    if (typed.greaterThan(ceiling)) return t.pos.feeTooLarge;

    // A lira fee with no exchange rate converts to nothing: `feeInUsd`
    // returns 0.00 rather than guess a rate. Said out loud and refused,
    // because now that lira is the DEFAULT this is the ordinary path, and a
    // charge that silently vanishes from the total is money the shop typed
    // in and did not take.
    if (counterFee.currency === 'LBP' && typed.greaterThan(0) && !exchangeRateValue) {
      return t.delivery.feeNoRate;
    }

    return null;
  })();

  /// The total in lira - the figure the Current Order shows and the one
  /// the server will charge. Handed to the payment dialog so the amount the
  /// cashier asks the customer for is the amount the order came to, rather
  /// than the rounded dollar total converted back into a slightly different
  /// number at the last moment.
  const totalLbp = frozenOrder && activeOrder?.totalLbp
    ? activeOrder.totalLbp
    : exchangeRateValue
      ? cartSubtotalLbp(lines, exchangeRateValue).plus(toDecimal(deliveryFeeLbp)).toFixed(0)
      : null;

  /// The dollars the lira bill comes to, quoted ONCE - the lira total divided
  /// by the rate, rounded to the cent - which is exactly the dollar figure
  /// the server accepts as settling it. Not the rounded dollar lines added
  /// up: three lines of $3.21 make $9.63 on paper while 861,000 LBP is
  /// $9.62, and a cashier paying the figure shown must never be a cent short.
  const total =
    totalLbp && exchangeRateValue
      ? toDecimal(totalLbp).dividedBy(exchangeRateValue).toDecimalPlaces(2)
      : itemsSubtotal.plus(toDecimal(deliveryFeeUsd)).toDecimalPlaces(2);

  /// Lines the cashier typed as a money amount, which have to become a weight
  /// before a butcher can cut them. Listed on screen, not converted quietly.
  const convertedLines = useMemo(
    () =>
      lines
        .filter((line) => line.quantityMode === 'CUSTOM_AMOUNT')
        .map((line) => ({
          id: line.id,
          name: locale === 'ar' ? line.nameAr : line.nameEn,
          amount: formatUsd(line.customAmount ?? '0'),
          quantity: formatQuantity(orderQuantityFor(line)),
        })),
    [lines, locale],
  );

  /// Why a delivery cannot leave right now, if it cannot. A delivery is
  /// created on the SERVER - it freezes prices, reserves stock and prints -
  /// so it needs a connection and the permission, and both are said out loud
  /// rather than left as a greyed-out button.
  const dispatchBlocker = !feeMakesDelivery
    ? null
    : !canTakeDelivery
      ? t.delivery.noPermission
      : offline
        ? t.delivery.offlineBlocked
        : null;

  // =========================================================================
  // Holding and restoring
  // =========================================================================

  const submitHold = async (details: HoldDetails) => {
    setHoldError(null);

    try {
      await holdCart.mutateAsync({
        customerId: details.customerId,
        label: details.label || null,
        customerName: details.customerName || null,
        phone: details.phone || null,
        orderNote: details.orderNote || null,
        items: lines.map((line) => ({
          productId: line.productId,
          variantId: line.variantId,
          quantityMode: line.quantityMode,
          quantity: line.quantity,
          ...(line.quantityMode === 'CUSTOM_AMOUNT'
            ? { customAmount: line.customAmount ?? '0' }
            : {}),
          note: line.note,
          ...(line.comboGroupKey ? { comboOfferId: line.comboOfferId, comboGroupKey: line.comboGroupKey } : {}),
        })),
      });

      setHoldOpen(false);
      resetTill();
      toast.success(t.held.heldState, t.held.holdBody);
    } catch (caught) {
      setHoldError(caught instanceof ApiError ? caught.message : t.errors.unexpectedBody);
    }
  };

  const beginRestore = async (heldId: string, version: number, customerId: string | null) => {
    setRestoringId(heldId);

    try {
      // Read before claiming the hold: a failed customer lookup must leave it
      // available on the spike, with its customer still attached.
      restoredCustomer.current = customerId ? (await api.get<{ customer: Customer }>(`/customers/${customerId}`)).customer : null;
      const result = await restoreHeld.mutateAsync({ id: heldId, version });

      // Anything moved while it waited: the cashier has to see it and say so
      // before the cart goes anywhere near a payment dialog.
      if (result.warnings.length > 0) {
        setPendingRestore(result);
        return;
      }

      applyRestore(result);
    } catch (caught) {
      toast.error(caught instanceof ApiError ? caught.message : t.errors.unexpectedBody);
    } finally {
      setRestoringId(null);
    }
  };

  /// Put a restored cart on the till, at TODAY's prices.
  ///
  /// The held price is deliberately not used: the customer is about to pay,
  /// and the shop charges what it charges today. The difference is what the
  /// warnings dialog just made the cashier say out loud.
  const applyRestore = (result: RestoreResult) => {
    const unavailable = new Set(
      result.warnings.filter((w) => w.kind === 'UNAVAILABLE').map((w) => w.productId),
    );
    const todaysPrice = new Map(
      result.warnings
        .filter((w) => w.kind === 'PRICE_CHANGED' && w.currentPriceUsd)
        .map((w) => [w.productId, w.currentPriceUsd!]),
    );

    replaceLines(
      result.heldOrder.items
        .filter((item) => !unavailable.has(item.productId))
        .map((item) =>
          cartLineFrom({
            productId: item.productId,
            variantId: item.variantId,
            nameEn: item.nameEn,
            nameAr: item.nameAr,
            variantNameEn: item.variantNameEn,
            variantNameAr: item.variantNameAr,
            variantLabelEn: item.variantLabelEn,
            variantLabelAr: item.variantLabelAr,
            sku: item.sku,
            sellingUnit: item.sellingUnit,
            unitPrice: todaysPrice.get(item.productId) ?? item.unitPriceUsd,
            // Today's effective lira, when the server re-quoted; a combo
            // component keeps its held allocation (the offer is still live,
            // or the line was dropped above).
            unitPriceLbp: item.comboGroupKey
              ? (item.unitPriceLbp ?? null)
              : todaysPrice.get(item.productId) && exchangeRateValue
                ? toDecimal(todaysPrice.get(item.productId)!).times(toDecimal(exchangeRateValue)).toDecimalPlaces(0).toFixed(0)
                : (item.unitPriceLbp ?? null),
            quantityMode: item.quantityMode,
            quantity: item.quantity,
            customAmount: item.customAmountUsd,
            note: item.note,
            basePriceLbp: item.basePriceLbp,
            promotionType: item.comboGroupKey ? item.promotionType : todaysPrice.has(item.productId) ? 'NONE' : item.promotionType,
            discountPercent: item.comboGroupKey ? null : todaysPrice.has(item.productId) ? null : item.discountPercent,
            comboOfferId: item.comboOfferId,
            comboGroupKey: item.comboGroupKey,
            comboNameEn: item.comboNameEn,
            comboNameAr: item.comboNameAr,
            comboOfferPriceLbp: item.comboOfferPriceLbp,
            comboRegularPriceLbp: item.comboRegularPriceLbp,
            lineTotalLbp: item.lineTotalLbp,
          }),
        ),
      result.heldOrder.orderNote ?? '',
    );

    setRestoredFromHeldId(result.heldOrder.id);
    setCustomer(restoredCustomer.current);
    setPendingRestore(null);
    setHeldDrawerOpen(false);
    toast.success(t.held.restored, t.held.restoredBody);
  };

  // =========================================================================
  // Delivery
  // =========================================================================

  /// Dispatch the Current Order as a delivery: freeze it, reserve the stock,
  /// print the receipt the driver takes - and move NO money. The delivery
  /// then waits in the Delivery View as «بانتظار الحساب».
  const submitDispatch = async (skipPreparation: boolean) => {
    setDispatchError(null);

    try {
      const response = await dispatchDelivery.mutateAsync({
        items: lines.map((line) => ({
          productId: line.productId,
          variantId: line.variantId,
          quantityMode: line.quantityMode,
          quantity: line.quantity,
          ...(line.quantityMode === 'CUSTOM_AMOUNT'
            ? { customAmount: line.customAmount ?? '0' }
            : {}),
          note: line.note,
          ...(line.comboGroupKey ? { comboOfferId: line.comboOfferId, comboGroupKey: line.comboGroupKey } : {}),
        })),
        // As typed, in lira - the server converts once and freezes the rate.
        deliveryFeeAmount: counterFee.amount.trim(),
        deliveryFeeCurrency: 'LBP',
        note: orderNote.trim() || null,
        ...(skipPreparation ? { preparationDispatch: 'SKIP' as const } : {}),
        // The same key for every retry, so the driver cannot go out twice.
        idempotencyKey,
      });

      setDispatchOpen(false);
      resetTill();
      setSentOrder(response.order);
      // The receipt the driver takes, rendered from the order's OWN frozen
      // snapshot - no sale exists and none is faked - plus the preparation
      // tickets when the plan owes any. Same off-screen pipeline as a sale.
      setCompletedSale(dispatchReceiptSale(response.order));
      setPrintPlan(response.printPlan ?? null);
      toast.success(t.delivery.dispatched, response.order.orderNumber);
    } catch (caught) {
      setDispatchError(caught instanceof ApiError ? caught.message : t.errors.unexpectedBody);
    }
  };

  /// Open the right dialog for what the Current Order IS, minting the key
  /// that protects this attempt. A positive fee means a delivery leaves the
  /// shop unpaid; everything else takes payment now.
  const startCheckout = () => {
    if (lines.length === 0 || loadingContext || loadingOrder || preservationId || (frozenOrder && (offline || activeOrder?.deliveryFeePending))) return;
    setPaymentError(null);
    setOrderError(null);
    setDispatchError(null);
    setIdempotencyKey(newIdempotencyKey());

    if (feeMakesDelivery) {
      setDispatchOpen(true);
      return;
    }

    setPaymentOpen(true);
  };

  /// Clear everything the last customer left behind.
  const resetTill = () => {
    clearCart();
    setPrintPlan(null);
    setFeeDraft('');
    setCounterFee(freshCounterFee);
    setCustomer(null);
    setActiveOrder(null);
    setRestoredFromHeldId(null);
    setOrderError(null);
    if (orderIdParam) setSearchParams({}, { replace: true });
  };

  /// Does anything in the cart route to a preparation bench? Answered by the
  /// cart domain's one sanctioned predicate - the checkbox appears exactly
  /// when a ticket would otherwise be written.
  const hasPreparationItems = cartNeedsPreparation(lines, products, categories);

  const submitPayment = async (
    payments: PaymentDraft[],
    changeCurrency: Currency,
    reach: { fulfilment: 'PICKUP' | 'ON_TABLE' | 'DELIVERY'; tableLabel: string | null } | null,
    skipPreparation: boolean,
    skipReceipt = false,
  ) => {
    setPaymentError(null);
    if (frozenOrder && activeOrder) {
      if (offline || activeOrder.deliveryFeePending || collectCurrentOrder.isPending) return;
      try {
        const response = await collectCurrentOrder.mutateAsync({ orderId: activeOrder.id, payments, changeCurrency, idempotencyKey, ...(skipReceipt ? { skipReceipt: true } : {}) });
        setPaymentOpen(false);
        resetTill();
        setCompletedSale(response.sale);
        setPrintPlan(response.printPlan ?? null);
        toast.success(t.payment.completed, response.sale.receiptNumber);
      } catch (caught) { setPaymentError(caught instanceof ApiError ? caught.message : t.errors.unexpectedBody); }
      return;
    }

    const payload = {
      items: lines.map((line) => ({
        productId: line.productId,
        // The type. The server prices the line from ITS row, not from what
        // this payload claims the price was.
        variantId: line.variantId,
        quantityMode: line.quantityMode,
        quantity: line.quantity,
        ...(line.quantityMode === 'CUSTOM_AMOUNT' ? { customAmount: line.customAmount ?? '0' } : {}),
        note: line.note,
        // A combo component names its offer and its group; the server
        // re-validates the group and prices it from the offer.
        ...(line.comboGroupKey ? { comboOfferId: line.comboOfferId, comboGroupKey: line.comboGroupKey } : {}),
      })),
      payments,
      changeCurrency,
      note: orderNote.trim() || null,
      // Phase 7. Sent whenever a customer is chosen, not only for credit -
      // it is what makes a purchase history possible.
      customerId: customer?.id ?? null,
      // --- operational changes ---
      ...(toDecimal(deliveryFeeUsd).greaterThan(0)
        ? // BOTH: the lira the shop charged, which the server adds to the
          // total, and the dollars it comes to, which the accounts read.
          { deliveryFeeUsd, deliveryFeeLbp }
        : {}),
      ...(activeOrder ? { deliveryOrderId: activeOrder.id } : {}),
      // HOW THE ORDER REACHES THE CUSTOMER. A fee makes it a delivery; an
      // order-linked sale takes the order's type server-side; otherwise it
      // is exactly what the cashier chose in the dialog. The server
      // re-derives and enforces all of this - these fields are the claim,
      // not the law.
      ...(activeOrder
        ? {}
        : toDecimal(deliveryFeeLbp || '0').greaterThan(0)
          ? { fulfilment: 'DELIVERY' as const }
          : reach
            ? {
                fulfilment: reach.fulfilment,
                ...(reach.fulfilment === 'ON_TABLE' ? { tableLabel: reach.tableLabel } : {}),
              }
            : {}),
      ...(restoredFromHeldId ? { heldOrderId: restoredFromHeldId } : {}),
      // The cashier's explicit order-level exception. Sent only when SKIP -
      // absence means NORMAL, exactly as it does for an older build.
      ...(skipPreparation ? { preparationDispatch: 'SKIP' as const } : {}),
      // «بدون إيصال» - stored on the sale, so the server's print plan leaves
      // the receipt out for every reader of it. Absent means print.
      ...(skipReceipt ? { skipReceipt: true } : {}),
      // The same key for every retry of this order.
      idempotencyKey,
    };

    // --- no connection: the sale is saved here, in full ---------------------
    //
    // Note what is NOT done: no attempt is made to send it first and fall back
    // on failure. A request that hangs for thirty seconds before failing is a
    // cashier standing in front of a customer doing nothing. The till already
    // knows it is offline, so it writes the sale down and moves on.
    if (offline) {
      if (!offlineStorageAvailable()) {
        setPaymentError(t.offline.cannotOffline);
        return;
      }

      const shiftId = shiftState?.shift?.id;
      if (!shiftId) {
        setPaymentError(t.offline.noShiftOffline);
        return;
      }

      // AND NOT INTO YESTERDAY'S DRAWER, even with no connection.
      //
      // This is the one place the till has to refuse something on its own,
      // because there is no server to ask. Queuing the sale here would write
      // a row that names a shift whose day has ended, and the server refuses
      // exactly that on sync - so the cashier would be told the sale failed
      // hours later, with the customer long gone and the money already in the
      // till. Refusing now, while they can still act, is the honest moment.
      //
      // `businessDayEnded` is the last answer the SERVER gave, which is the
      // best available while offline: it was true when the connection was
      // lost and nothing since can have made the day un-end.
      if (businessDayEnded) {
        setPaymentError(t.payment.dayEndedShort);
        return;
      }

      try {
        const device = await deviceId();
        // The key generated for this checkout attempt IS the offline id, so a
        // sale that was half-sent online and then queued cannot be recorded
        // twice.
        const offlineId = idempotencyKey || newOfflineId(device);
        // A receipt number the server reserved for this till while the
        // connection was good. Spent now, so the receipt can print now; the
        // queued sale carries it, and the server records the sale under it.
        // Null when the pool is empty: the sale is still saved, the paper
        // waits for the connection.
        const receiptNumber = await takeReservedReceipt();
        const createdAt = new Date().toISOString();

        await enqueueSale({
          offlineId,
          payload: { ...payload, idempotencyKey: offlineId, ...(receiptNumber ? { receiptNumber } : {}) },
          createdAt,
          shiftId,
          totalUsd: total.toFixed(2),
          itemCount: lines.length,
          attempts: 0,
        });

        // No paper when the cashier said «بدون إيصال» - the queued sale
        // carries the choice too, so syncing it later prints nothing either.
        const receiptSale =
          receiptNumber && exchangeRateValue && !skipReceipt
            ? offlineReceiptSale({
                offlineId,
                receiptNumber,
                lines,
                payments,
                changeCurrency,
                rate: exchangeRateValue,
                deliveryFeeUsd,
                deliveryFeeLbp,
                note: orderNote.trim() || null,
                fulfilment: toDecimal(deliveryFeeLbp || '0').greaterThan(0) ? 'DELIVERY' : (reach?.fulfilment ?? null),
                tableLabel: reach?.fulfilment === 'ON_TABLE' ? reach.tableLabel : null,
                cashier: { id: currentUser?.id ?? '', fullName: currentUser?.fullName ?? '', username: currentUser?.username ?? '' },
                shift: { id: shiftId, shiftNumber: shiftState?.shift?.shiftNumber ?? '' },
                createdAt,
              })
            : null;

        setPaymentOpen(false);
        resetTill();
        setSavedOffline({ offlineId, totalUsd: total.toFixed(2) });
        if (receiptSale) {
          setOfflineReceipt({ offlineId, sale: receiptSale });
          toast.success(t.offline.soldOffline, t.offline.soldOfflineBody);
        } else if (skipReceipt) {
          // No paper by choice, not for want of a number.
          toast.success(t.offline.soldOffline);
        } else {
          toast.success(t.offline.soldOffline, t.offline.noReceiptNumber);
        }
      } catch {
        // The one case where the sale genuinely cannot be recorded. The cart
        // is left alone so nothing is lost.
        setPaymentError(t.offline.cannotOffline);
      }

      return;
    }

    try {
      const response = await completeSale.mutateAsync(payload);

      // Only once the server has confirmed does the cart get emptied. If the
      // request failed, the order is still on screen and can be tried again.
      setPaymentOpen(false);
      resetTill();
      setCompletedSale(response.sale);
      // Printing starts only now, with the sale already committed. Nothing
      // below this line can reach back into it.
      setPrintPlan(response.printPlan ?? null);

      // NOTHING OPENS. The receipt and the preparation ticket both go
      // straight to their printers, in the background, off the plan above.
      //
      // They used to open as full-screen previews with Print and Send
      // buttons, which put two dialogues between the cashier and the next
      // customer for documents that were going to the same two printers
      // every time. The paper is the point; the preview was ceremony.
      // Manual previews and reprints still exist where they belong - Orders,
      // the ticket history and the designers.
      toast.success(t.payment.completed, response.sale.receiptNumber);
    } catch (error) {
      setPaymentError(
        error instanceof ApiError ? error.message : t.errors.unexpectedBody,
      );
    }
  };

  const busy = completeSale.isPending || dispatchDelivery.isPending || collectCurrentOrder.isPending || loadingOrder;

  // The frozen fee of a menu order under review, as it was quoted to the
  // customer - in lira, and in the dollars its own rate makes of it.
  const reviewFeeLbp = reviewOrder && reviewOrder.orderType === 'DELIVERY' ? reviewOrder.deliveryFeeLbp : undefined;
  const reviewFeeUsd =
    reviewOrder && reviewOrder.orderType === 'DELIVERY' && toDecimal(reviewOrder.exchangeRateUsdToLbp).greaterThan(0)
      ? toDecimal(reviewOrder.deliveryFeeLbp).dividedBy(toDecimal(reviewOrder.exchangeRateUsdToLbp)).toDecimalPlaces(2).toFixed(2)
      : undefined;

  // ---------------------------------------------------------------------
  // The Current Order, built once and placed twice.
  //
  // On a wide screen it is the second grid track; on a narrow one the drawer
  // renders exactly this. Building it here rather than duplicating the markup
  // is what stops the two ever drifting apart - a delivery field added to one
  // and forgotten in the other is a field a cashier cannot reach on a tablet.
  // ---------------------------------------------------------------------
  const orderColumn = (flow: 'fill' | 'natural') => (
    <>
      {/* Selling without an open shift is refused by the server, so the
          cashier is told here rather than after building a whole order. */}
      {!hasOpenShift ? (
        <div className="shrink-0 rounded-card border border-burgundy-red/30 bg-burgundy-red/10 p-3.5">
          <p className="flex items-center gap-2 text-sm font-semibold text-burgundy-red">
            <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
            {t.payment.shiftRequired}
          </p>
          <p className="mt-1 text-xs text-ink">{t.payment.shiftRequiredHint}</p>
          <Link to="/shifts" className="btn-primary mt-2.5 w-full">
            {t.payment.goToShift}
          </Link>
        </div>
      ) : businessDayEnded ? (
        // The drawer exists, so the answer is not "open a shift". It stays on
        // screen behind the blocking dialog so the reason is still there after
        // the cashier navigates away and comes back.
        <div className="shrink-0 rounded-card border border-antique-gold/50 bg-antique-gold/10 p-3.5">
          <p className="flex items-center gap-2 text-sm font-semibold text-charcoal">
            <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
            {t.payment.dayEndedTitle}
          </p>
          <p className="mt-1 text-xs text-ink">{t.payment.dayEndedShort}</p>
          {canCloseShift ? (
            <Link to="/shifts" className="btn-primary mt-2.5 w-full">
              {t.payment.dayEndedAction}
            </Link>
          ) : null}
        </div>
      ) : null}

      {/* الطلب الحالي / حسابات التوصيل - the column's two views. Absent for
          somebody who cannot see the delivery board; their column is simply
          the Current Order, as it always was. */}
      {canSeeAccounts ? (
        <PosViewToggle view={posView} onChange={(view) => {
          setPosView(view);
          setSearchParams(params => {
            if (view === 'ACCOUNTS') params.set('view', 'accounts');
            else params.delete('view');
            return params;
          }, { replace: true });
        }} pendingCount={pendingAccounts} />
      ) : null}

      {posView === 'ACCOUNTS' && canSeeAccounts ? (
        // The quick collection panel IN PLACE OF the cart - never instead of
        // the cart's STATE, which sits untouched in its store until the
        // cashier switches back.
        <div
          className={clsx(
            'flex min-w-0 flex-col',
            flow === 'fill' ? 'min-h-0 flex-1' : 'shrink-0',
          )}
        >
          <DeliveryAccountsPanel
            flow={flow}
            onCollect={setCollectingOrder}
            onPreview={setPreviewOrder}
          />
        </div>
      ) : (
        <>
      {/* The order this till is settling - a pickup taken over the phone, or
          a legacy delivery - named so nobody wonders whose figures these are. */}
      {activeOrder && !frozenOrder ? (
        <div className="flex min-w-0 shrink-0 items-center gap-2 rounded-card border border-butcher-green/40 bg-butcher-green/10 p-3 text-sm">
          <Truck className="h-4 w-4 shrink-0 text-butcher-green" aria-hidden="true" />
          <span className="min-w-0 break-words text-ink">
            {t.delivery.reopened.replace('{number}', activeOrder.orderNumber)}
            {activeOrder.customer ? ` — ${activeOrder.customer.name}` : ''}
          </span>
        </div>
      ) : null}

      {/* An online menu order under review: whose it is, and the way out. */}
      {reviewOrder ? (
        <MenuOrderReviewBanner
          order={reviewOrder}
          busy={review.busy}
          canConfirm={canConfirmMenuOrder}
          canReject={canRejectMenuOrder}
          onConfirm={() => void review.confirmNow()}
          onReject={review.openReject}
          onExit={exitReview}
        />
      ) : null}
      {menuOrderError ? (
        <p className="shrink-0 break-words rounded-card border border-burgundy-red/30 bg-burgundy-red/10 p-3 text-sm text-burgundy-red" role="alert">
          {menuOrderError instanceof ApiError ? menuOrderError.message : t.errors.unexpectedBody}
        </p>
      ) : null}

      {/* A positive fee has made this a DELIVERY: say so where the cashier is
          looking, before the button at the bottom says it again. */}
      {feeMakesDelivery ? (
        <div className="flex min-w-0 shrink-0 items-center gap-2 rounded-card border border-butcher-green/40 bg-butcher-green/10 p-3 text-sm">
          <Truck className="h-4 w-4 shrink-0 text-butcher-green" aria-hidden="true" />
          <span className="min-w-0 break-words font-medium text-ink">
            {t.delivery.feeMakesDelivery}
          </span>
        </div>
      ) : null}

      {loadingOrder ? <p role="status" className="text-sm text-ink-muted">{t.common.loading}</p> : null}
      {orderError && loadingContext ? <div className="flex gap-2"><button className="btn-secondary" onClick={() => setHandoffAttempt(value => value + 1)}>{t.common.retry}</button><button className="btn-secondary" disabled={Boolean(preservationId)} onClick={() => setSearchParams({}, { replace: true })}>{t.common.back}</button></div> : null}
      {orderError ? (
        <p
          className="shrink-0 break-words rounded-card border border-burgundy-red/30 bg-burgundy-red/10 p-3 text-sm text-burgundy-red"
          role="alert"
        >
          {orderError}
        </p>
      ) : null}

      {reopenError ? (
        <p
          className="shrink-0 break-words rounded-card border border-burgundy-red/30 bg-burgundy-red/10 p-3 text-sm text-burgundy-red"
          role="alert"
        >
          {reopenError instanceof ApiError ? reopenError.message : t.errors.unexpectedBody}
        </p>
      ) : null}

      {/* `min-h-0` so the cart's own list scrolls inside it rather than
          stretching this column past the bottom of the screen. In the drawer
          this wrapper simply grows and the drawer scrolls. */}
      <div
        className={clsx(
          'flex min-w-0 flex-col',
          flow === 'fill' ? 'min-h-0 flex-1' : 'shrink-0',
        )}
      >
        <CartPanel
          flow={flow}
          context={frozenOrder && activeOrder ? <ConfirmedMenuOrder order={activeOrder} /> : null}
          onSetAside={frozenOrder ? resetTill : undefined}
          setAsideDisabled={confirmFee.isPending || busy}
          showDeliveryFee={frozenOrder && activeOrder?.fulfilment === 'DELIVERY' && !activeOrder.deliveryFeePending}
          lines={lines}
          orderNote={orderNote}
          exchangeRate={exchangeRateValue}
          frozenTotalUsd={frozenOrder ? total.toFixed(2) : undefined}
          canCheckout={reviewOrder ? canConfirmMenuOrder && !review.busy : canTakeMoney && !busy && !loadingContext && !preservationId}
          deliveryFeeUsd={reviewOrder ? reviewFeeUsd : deliveryFeeUsd}
          deliveryFeeLbp={reviewOrder ? reviewFeeLbp : deliveryFeeLbp}
          readOnly={Boolean(reviewOrder) || saleLocked}
          {...(frozenOrder && activeOrder?.fulfilment === 'DELIVERY' && activeOrder.deliveryFeePending
            ? { feeEditor: { amount: feeDraft, onAmountChange: setFeeDraft, confirmation: {
                disabled: !canTakeDelivery || offline,
                busy: confirmFee.isPending,
                onConfirm: () => {
                  if (!/^\d{1,12}$/.test(feeDraft) || confirmFee.isPending) return;
                  setOrderError(null);
                  // Confirming the fee IS the dispatch: the driver is about to
                  // leave with the goods, so the paper goes out with them -
                  // the customer receipt they carry and the bench tickets -
                  // exactly as a till-dispatched delivery does. And the
                  // delivery LEAVES the Current Sale: it is an account out
                  // with the driver now, collected from Delivery Accounts like
                  // every other, and the till is free for the next customer.
                  // Only after the server answered - a refused fee keeps the
                  // order on the till, with the error, and prints nothing.
                  void confirmFee
                    .mutateAsync({ orderId: activeOrder.id, amountLbp: feeDraft })
                    .then(result => {
                      releasedOrder.current = result.order.id;
                      resetTill();
                      // AFTER the reset, deliberately: resetTill clears both
                      // of these along with everything else the last customer
                      // left, and this paper belongs to the delivery leaving
                      // now. The receipt is rendered from the order's OWN
                      // frozen snapshot through the same helper the till's own
                      // dispatch uses - no sale exists yet and none is faked.
                      // The server only answers with a plan once the fee is
                      // committed, so nothing prints for a fee that was
                      // refused.
                      setCompletedSale(dispatchReceiptSale(result.order));
                      setPrintPlan(result.printPlan ?? null);
                      toast.success(t.menu.orders.feeSentToAccounts, result.order.orderNumber);
                    })
                    .catch((caught: unknown) => setOrderError(caught instanceof Error ? caught.message : t.errors.unexpectedBody));
                },
              } } }
            : activeOrder || reviewOrder
            ? // Settling an existing order: its fee froze when it was taken,
              // and a second editable fee would put two numbers on one paper.
              {}
            : {
                feeEditor: {
                  amount: counterFee.amount,
                  onAmountChange: (amount: string) =>
                    setCounterFee((current) => ({ ...current, amount })),
                  problem: counterFeeProblem,
                  quickFill:
                    standingFee && canTakeDelivery
                      ? {
                          label: t.delivery.useStandingFee.replace('{amount}', standingFee),
                          onFill: () =>
                            setCounterFee((current) => ({ ...current, amount: standingFee })),
                        }
                      : null,
                },
              })}
          blockedReason={reviewOrder ? null : frozenOrder && activeOrder?.deliveryFeePending ? t.menu.orders.feePending : frozenOrder && offline ? t.delivery.offlineBlocked : (dispatchBlocker ?? counterFeeProblem)}
          checkoutLabel={
            reviewOrder
              ? t.menu.orders.confirm
              : activeOrder && !frozenOrder
                ? t.delivery.collectPayment
                : feeMakesDelivery
                  ? t.delivery.dispatchConfirm
                  : undefined
          }
          {...(canHold && !activeOrder && !reviewOrder ? { onHold: () => setHoldOpen(true) } : {})}
          onEditLine={(line) => {
            setKeypadProduct(null);
            setEditingLine(line);
          }}
          onRemoveLine={(line) => removeLine(line.id)}
          onOrderNoteChange={setOrderNote}
          onClear={() => setConfirmClear(true)}
          onCheckout={reviewOrder ? () => void review.confirmNow() : startCheckout}
        />
      </div>
        </>
      )}
    </>
  );

  return (
    // One grid. The first track takes what is left; the second is the order
    // column, present only from `till` up. Both are minmax(0, ...) so neither
    // can be widened past the viewport by its own contents.
    <div
      className="grid h-full min-h-0 w-full min-w-0 grid-cols-[minmax(0,1fr)] gap-4 till:grid-cols-[minmax(0,1fr)_minmax(0,22rem)] 2xl:till:grid-cols-[minmax(0,1fr)_minmax(0,24rem)]"
    >
      <MenuOrderArrival />
      {/* ================= products ================= */}
      <div className="flex min-h-0 min-w-0 flex-col gap-3">
        {/* --- search ---------------------------------------------------- */}
        <div className="flex min-w-0 shrink-0 gap-2">
          <div className="relative min-w-0 flex-1">
            <Search
              className="pointer-events-none absolute inset-y-0 start-3 my-auto h-4 w-4 text-ink-muted"
              aria-hidden="true"
            />
            <input
              ref={searchRef}
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              onKeyDown={onSearchKeyDown}
              placeholder={t.pos.searchPlaceholder}
              aria-label={t.pos.searchPlaceholder}
              className="input h-touch ps-10 pe-10 text-base"
            />
            {search ? (
              <button
                type="button"
                onClick={() => {
                  setSearch('');
                  searchRef.current?.focus();
                }}
                className="absolute inset-y-0 end-0 flex w-10 items-center justify-center text-ink-muted hover:text-ink"
                aria-label={t.common.close}
              >
                <X className="h-4 w-4" aria-hidden="true" />
              </button>
            ) : null}
          </div>

          {/* The spike of carts waiting to come back. */}
          {canSeeHeld ? (
            <button
              type="button"
              onClick={() => setHeldDrawerOpen(true)}
              className="flex h-touch shrink-0 items-center gap-2 rounded-control border border-line-strong bg-surface px-4 text-sm font-semibold text-ink-muted transition-colors hover:bg-surface-sunken"
            >
              <PauseCircle className="h-4 w-4" aria-hidden="true" />
              <span className="hidden sm:inline">{t.held.heldOrders}</span>
            </button>
          ) : null}
        </div>

        {/* --- category tabs --------------------------------------------- */}
        <CategoryRail
          ariaLabel={t.products.tabCategories}
          activeId={categoryId}
          onSelect={setCategoryId}
          // NO "All" tab. The cashier always works inside one category, so
          // the grid is the shop's own arrangement of that shelf rather than
          // an undifferentiated wall of every product. Search and barcode
          // scanning still reach the whole sellable catalogue - see the
          // search box above, which clears the category itself.
          items={[
            // The virtual offers chip FIRST - and only while something is
            // on offer. Its count is the number of promotional cards: the
            // combos plus the discounted products, each product once.
            ...(offersChipCount > 0
              ? [{ id: OFFERS_TAB, label: `🔥 ${t.offers.offersTab}`, count: offersChipCount, color: null }]
              : []),
            ...posCategories.map((category) => ({
              id: category.id,
              label: locale === 'ar' ? category.nameAr : category.nameEn,
              count: category.productCount,
              color: category.color,
            })),
          ]}
        />

        {/* --- grid ------------------------------------------------------- */}
        {/* The one region that scrolls on this side, and it scrolls
            VERTICALLY only. `min-w-0` so a tile can never widen it. */}
        {/* `product-panel` makes this the box the grid inside it measures
            itself against, so the cards re-flow when the cart opens rather
            than when the window resizes. */}
        <div className="scrollbar-brand product-panel min-h-0 min-w-0 flex-1 overflow-y-auto overflow-x-hidden pe-1">
          {error ? (
            <div className="card">
              <ErrorState error={error} onRetry={() => void refetch()} />
            </div>
          ) : !isLoading && shownProducts.length === 0 && !(onOffersTab && combos.length > 0) ? (
            <div className="card">
              <EmptyState title={t.pos.noResults} description={t.pos.noResultsHint} />
            </div>
          ) : (
            <ProductGrid
              products={shownProducts}
              loading={isLoading}
              onSelect={openKeypadFor}
              combos={onOffersTab ? combos : []}
              onSelectCombo={(combo: ComboOffer) => {
                if (reviewOrder || saleLocked) {
                  toast.info(frozenOrder ? t.menu.orders.frozenSale : t.menu.orders.frozen);
                  return;
                }
                addCombo(combo);
                searchRef.current?.focus();
              }}
              // The same rate the cart, the payment dialog and the header use.
              exchangeRate={exchangeRateValue}
            />
          )}
        </div>

        {/* Phase 9. When the list came from the device rather than the server,
            say so and say how old it is. A cashier selling from a three-day-old
            price list should know that is what they are doing. */}
        {fromCache && capturedAt ? (
          <p className="shrink-0 truncate text-xs text-ink-muted">
            {t.offline.catalogueAge.replace('{when}', formatDateTime(capturedAt, locale))}
          </p>
        ) : null}

        {/* Offline with no snapshot at all: the one state in which this till
            genuinely cannot sell, said plainly rather than shown as an empty
            product grid. */}
        {catalogueUnavailable ? (
          <p className="shrink-0 break-words rounded-card border border-burgundy-red/30 bg-burgundy-red/10 p-3 text-sm text-burgundy-red">
            {t.offline.catalogueMissing}
          </p>
        ) : null}

        {/* The narrow-screen order bar, pinned to the foot of THIS column so
            the product grid scrolls behind it and the way to take payment is
            never scrolled out of reach. Hidden once the order has a column of
            its own. */}
        <CartSummaryBar
          itemCount={lines.length}
          totalUsd={total.toFixed(2)}
          onOpen={() => setCartDrawerOpen(true)}
        />
      </div>

      {/* ================= the order ================= */}
      {/* From `till` up: its own column, always visible, scrolling inside
          itself. Below: nothing here, and the drawer below carries it. */}
      <div className="hidden min-h-0 min-w-0 flex-col gap-3 till:flex">{orderColumn('fill')}</div>

      <CartDrawer open={cartDrawerOpen} onClose={() => setCartDrawerOpen(false)}>
        {orderColumn('natural')}
      </CartDrawer>

      {/* ================= dialogs ================= */}
      <QuantityKeypad
        open={Boolean(keypadProduct || editingLine)}
        product={keypadProduct}
        editingLine={editingLine}
        // The catalogue product behind the line being edited, so its types can
        // be offered again when the cashier wants to change one.
        editingProduct={
          editingLine ? (products.find((row) => row.id === editingLine.productId) ?? null) : null
        }
        // The same rate the header, the cart and the payment dialog read, so
        // the price on the variant button is the price on the cart line.
        exchangeRate={exchangeRateValue}
        onClose={closeKeypad}
        onSubmit={onKeypadSubmit}
      />

      <PaymentDialog
        open={paymentOpen}
        // Whether "no preparation" means anything for THIS cart, from the
        // same category routing the benches follow.
        hasPreparationItems={hasPreparationItems}
        // A delivery order past NEW has had its ticket cut already; the
        // dialog then states that instead of offering a checkbox that
        // cannot deliver.
        preparationAlreadySent={Boolean(activeOrder && activeOrder.status !== 'NEW')}
        onClose={() => setPaymentOpen(false)}
        totalUsd={total.toFixed(2)}
        totalLbp={totalLbp}
        exchangeRate={exchangeRateValue}
        submitting={completeSale.isPending || collectCurrentOrder.isPending}
        errorMessage={paymentError}
        creditAllowed={canSellOnCredit}
        customer={customer}
        onChooseCustomer={() => setCustomerPickerOpen(true)}
        // A positive fee makes this a delivery, whatever mode the till is in
        // - the fee is the business rule, the mode is only the screen.
        deliveryActive={activeOrder?.fulfilment === 'DELIVERY' || toDecimal(deliveryFeeLbp || '0').greaterThan(0)}
        deliveryFeeLbp={deliveryFeeLbp || null}
        // Paying an existing order: the order decided its type when it was
        // taken. Shown read-only; the server derives from the order row.
        orderFulfilment={activeOrder ? activeOrder.fulfilment : null}
        // A frozen DELIVERY settled here owes no paper (it left with the
        // driver), so «بدون إيصال» would decide nothing; everything else
        // prints a receipt and offers the choice.
        receiptWillPrint={!(frozenOrder && activeOrder?.fulfilment === 'DELIVERY')}
        onSubmit={(payments, changeCurrency, reach, skipPreparation, skipReceipt) =>
          void submitPayment(payments, changeCurrency, reach, skipPreparation, skipReceipt)
        }
      />

      <CustomerPicker
        open={customerPickerOpen}
        onClose={() => setCustomerPickerOpen(false)}
        onChoose={setCustomer}
        // The picker exists for credit sales - a delivery needs no customer
        // record at all any more.
        forCredit
      />

      {/* «استلام الحساب» from POS Delivery Accounts, using the delivery's
          own frozen figures.
          It shares nothing with the cart's payment dialog above: different
          entity, different endpoint, different state. */}
      <CollectAccountDialog
        order={collectingOrder}
        onClose={() => setCollectingOrder(null)}
      />

      {/* The review's reason dialog and off-screen paper. */}
      {review.elements}

      {/* The READ-ONLY receipt preview a card tap opens: the CANONICAL
          receipt modal - the same one Orders' $ icon uses - fed the order's
          frozen snapshot, identified by its order number, printing (only on
          its own explicit button) a CUSTOMER_RECEIPT copy on the shop's
          receipt printer, recorded against the ORDER. */}
      {previewOrder ? (
        <SaleReceipt
          open
          onClose={() => setPreviewOrder(null)}
          sale={dispatchReceiptSale(previewOrder)}
          orderId={previewOrder.id}
          shopNameEn={settings?.['business.name_en'] ?? 'Yaghi Butcher'}
          shopNameAr={settings?.['business.name_ar'] ?? 'ملحمة ياغي'}
          shopAddress={settings?.['business.address'] ?? ''}
          shopPhone={settings?.['business.phone'] ?? ''}
          receiptFooter={settings?.['business.receipt_footer'] ?? ''}
        />
      ) : null}

      <DeliveryDispatchDialog
        open={dispatchOpen}
        onClose={() => setDispatchOpen(false)}
        itemCount={lines.length}
        itemsSubtotalLbp={
          exchangeRateValue ? cartSubtotalLbp(lines, exchangeRateValue).toFixed(0) : null
        }
        feeLbp={deliveryFeeLbp}
        totalLbp={totalLbp}
        totalUsd={total.toFixed(2)}
        convertedLines={convertedLines}
        hasPreparationItems={hasPreparationItems}
        submitting={dispatchDelivery.isPending}
        errorMessage={dispatchError}
        onConfirm={(skipPreparation) => void submitDispatch(skipPreparation)}
      />

      <HoldOrderDialog
        open={holdOpen}
        onClose={() => setHoldOpen(false)}
        onConfirm={(details) => void submitHold(details)}
        submitting={holdCart.isPending}
        itemCount={lines.length}
        totalUsd={itemsSubtotal.toFixed(2)}
        customer={customer}
        orderNote={orderNote}
        errorMessage={holdError}
      />

      <HeldOrdersDrawer
        open={heldDrawerOpen}
        onClose={() => setHeldDrawerOpen(false)}
        restoringId={restoringId}
        cartHasItems={lines.length > 0}
        onRestore={(held) => {
          if (!canRestore || saleLocked) return;
          void beginRestore(held.id, held.version, held.customer?.id ?? null);
        }}
      />

      {pendingRestore ? (
        <RestoreWarningsDialog
          open
          warnings={pendingRestore.warnings}
          heldTotalUsd={pendingRestore.heldTotalUsd}
          currentTotalUsd={pendingRestore.currentTotalUsd}
          onAccept={() => applyRestore(pendingRestore)}
          onCancel={() => setPendingRestore(null)}
        />
      ) : null}

      {/* No post-sale preview of either document. Both are already on their
          way to their printers; see `NetworkPrintSheets` below, which draws
          them off-screen, photographs them and hands the pixels to the print
          queue without anything appearing over the till. */}

      {/* The documents this sale owes, rendered off-screen and photographed.
          Mounted only while there is a plan, and removed with the receipt. */}
      {offlineReceipt ? (
        <OfflineReceiptPrint
          offlineId={offlineReceipt.offlineId}
          sale={offlineReceipt.sale}
          shop={{ nameEn: settings?.['business.name_en'] ?? 'Yaghi Butcher', nameAr: settings?.['business.name_ar'] ?? 'ملحمة ياغي', address: settings?.['business.address'] ?? '', phone: settings?.['business.phone'] ?? '' }}
          onSettled={() => setOfflineReceipt(null)}
        />
      ) : null}
      {printPlan ? (
        <NetworkPrintSheets
          sale={completedSale}
          plan={printPlan}
          // Dropped the moment both documents have finished, so the next
          // customer's order is not built on top of the last one's print
          // state. The toasts have already been said by then and live in
          // their own store, so nothing the cashier still needs disappears
          // with it.
          onSettled={() => setPrintPlan(null)}
          shop={{
            nameEn: settings?.['business.name_en'] ?? 'Yaghi Butcher',
            nameAr: settings?.['business.name_ar'] ?? 'ملحمة ياغي',
            address: settings?.['business.address'] ?? '',
            phone: settings?.['business.phone'] ?? '',
          }}
        />
      ) : null}

      {/* A delivery that has just left, unpaid on purpose. Deliberately not a
          receipt preview: the paper is already printing and going with the
          driver, and what the cashier needs to know is where the account now
          lives. */}
      <Overlay
        open={Boolean(sentOrder)}
        onDismiss={() => setSentOrder(null)}
        label={t.delivery.dispatchedTitle}
        zIndexClass="z-40"
      >
        {sentOrder ? (
          <div className="w-full max-w-md rounded-card border border-line bg-surface p-5 shadow-raised">
            <h2 className="flex items-center gap-2 text-base font-semibold text-ink">
              <Truck className="h-5 w-5 text-butcher-green" aria-hidden="true" />
              {t.delivery.dispatchedTitle}
            </h2>

            <p className="mt-2 text-sm text-ink">
              {t.delivery.dispatchedBody.replace('{number}', sentOrder.orderNumber)}
            </p>

            {/* Where the money now stands: nowhere, yet - and that is the
                design, said plainly. */}
            <p className="mt-2 rounded-control bg-surface-soft p-3 text-xs text-ink-muted">
              {t.delivery.awaitingAccountHint}
            </p>

            <p className="numeric mt-3 text-lg font-bold text-butcher-green">
              {sentOrder.totalLbp
                ? formatLbp(sentOrder.totalLbp)
                : formatUsd(sentOrder.estimatedTotalUsd)}
            </p>

            <div className="mt-4 flex gap-2">
              <Link to="/pos?view=accounts" className="btn-secondary flex-1">
                {t.delivery.viewDeliveries}
              </Link>
              <button
                type="button"
                className="btn-primary flex-1"
                onClick={() => {
                  setSentOrder(null);
                  searchRef.current?.focus();
                }}
              >
                {t.delivery.newOrder}
              </button>
            </div>
          </div>
        ) : null}
      </Overlay>

      {/* A sale saved on this till because there was no connection. Shown in
          its own panel rather than as a receipt, because it does not have a
          receipt number yet and inventing one would be worse than saying so. */}
      <Overlay
        open={Boolean(savedOffline)}
        onDismiss={() => setSavedOffline(null)}
        label={t.common.offline}
        zIndexClass="z-40"
      >
        {savedOffline ? (
          <div className="w-full max-w-md">
            <OfflineSaleConfirmation
              offlineId={savedOffline.offlineId}
              totalUsd={savedOffline.totalUsd}
              onDone={() => setSavedOffline(null)}
            />
          </div>
        ) : null}
      </Overlay>

      <ConfirmDialog
        open={confirmClear}
        title={t.pos.clearCartTitle}
        message={t.pos.clearCartMessage}
        confirmLabel={t.pos.clearCartConfirm}
        cancelLabel={t.common.cancel}
        destructive
        onConfirm={() => {
          resetTill();
          setConfirmClear(false);
          searchRef.current?.focus();
        }}
        onCancel={() => setConfirmClear(false)}
      />

      {/* THE BLOCK. Last in the tree so nothing can render over it, and not
          dismissible: a cashier who could wave it away would carry on trying
          to sell and be refused one customer at a time by the server instead.

          The cart underneath is untouched - not cleared, not held, not
          completed - which is why the dialog says so in words. */}
      <BusinessDayEndedDialog
        open={businessDayEnded && Boolean(shiftState?.shift)}
        shiftNumber={shiftState?.shift?.shiftNumber ?? ''}
        businessDay={shiftState?.shift?.businessDay ?? ''}
        canClose={canCloseShift}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
